import { createFileRoute, Link } from "@tanstack/react-router";
import { absoluteUrl } from "@/config/sites";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { WP_REVIEWED_ISO, WP_REVIEWED_TH } from "@/data/workpermit";
import { WP_QA_ALL } from "@/data/workpermit/questions";
import { JsonLd } from "@/components/JsonLd";

const TITLE = "คำถามใบอนุญาตทำงาน ตม.30 ตม.47 Re-entry";
const DESC =
  "รวมคำถาม-คำตอบเรื่องใบอนุญาตทำงาน ต่ออายุ เปลี่ยนนายจ้าง รายงานตัว 90 วัน แจ้งที่พัก Re-entry Permit หนังสือรับรองถิ่นที่อยู่ และ ABTC";

export const Route = createFileRoute("/work-permit/questions/")({
  head: () => {
    const url = absoluteUrl("/work-permit/questions");
    return {
      meta: [
        { title: ctrTitle(TITLE) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  errorComponent: () => (
    <div className="mx-auto max-w-2xl p-8 text-center text-sm">
      โหลดหน้านี้ไม่สำเร็จ กรุณารีเฟรชอีกครั้ง
    </div>
  ),
  component: WpQuestionsPage,
});

function WpQuestionsPage() {
  const url = absoluteUrl("/work-permit/questions");
  const graph = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "FAQPage",
        "@id": `${url}#faq`,
        url,
        dateModified: WP_REVIEWED_ISO,
        mainEntity: WP_QA_ALL.slice(0, 50).map((q) => ({
          "@type": "Question",
          name: q.q,
          acceptedAnswer: { "@type": "Answer", text: q.a },
        })),
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: [".answer-summary", ".faq-answer"],
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "หน้าแรก", item: absoluteUrl("/") },
          {
            "@type": "ListItem",
            position: 2,
            name: "ใบอนุญาตทำงานและงานตรวจคนเข้าเมือง",
            item: absoluteUrl("/work-permit"),
          },
          { "@type": "ListItem", position: 3, name: "คำถามที่พบบ่อย", item: url },
        ],
      },
    ],
  };

  return (
    <>
      <JsonLd data={graph} />
      <Breadcrumbs
        items={[
          { label: "หน้าแรก", to: "/" },
          { label: "ใบอนุญาตทำงานและงานตรวจคนเข้าเมือง", to: "/work-permit" },
          { label: "คำถามที่พบบ่อย" },
        ]}
      />
      <section className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="mb-4 text-2xl font-bold">
          คำถามที่พบบ่อย — ใบอนุญาตทำงานและงานตรวจคนเข้าเมือง
        </h1>
        <p className="answer-summary mb-6 text-base leading-relaxed" data-speakable>
          รวมคำถาม {WP_QA_ALL.length} ข้อ เกี่ยวกับใบอนุญาตทำงาน การต่ออายุ การเปลี่ยนนายจ้าง
          การรายงานตัว 90 วัน (ตม.47) การแจ้งที่พัก (ตม.30) Re-entry Permit หนังสือรับรองถิ่นที่อยู่
          และบัตร ABTC ทุกคำตอบระบุหน่วยงานผู้รับและฐานกฎหมายที่อ้างอิงได้ ข้อมูล ณ {WP_REVIEWED_TH}
        </p>
        <ul className="list-disc space-y-1 pl-5 text-sm">
          {WP_QA_ALL.map((q) => (
            <li key={q.slug}>
              <Link
                to="/work-permit/q/$slug"
                params={{ slug: q.slug }}
                className="text-blue-700 hover:underline"
              >
                {q.q}
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
